import { useMemo } from 'react';
import NextImage from 'next/image';
import { CldImage } from 'next-cloudinary';
import { useResolvedLocalizedObject } from 'helpers/hooks/useResolvedLocalizedObject';
import { useProjectSettings } from 'frontastic';
import useDimensions from './hooks/useDimensions';
import defaultLoader, { netlifyLoader } from './loaders/default';
import { ImageProps } from './types';

// Fallback image to use when no valid source is provided

const getCropConfig = (gravity?: ImageProps['gravity']) => {
  if (gravity?.mode === 'custom' && gravity.coordinates) {
    return {
      type: 'fill' as const,
      x: gravity.coordinates.x,
      y: gravity.coordinates.y,
    };
  }
  return 'fill';
};

const Image = ({ media, ratio, gravity, suffix, src = '', width, height, alt = '', title, cropType, ...props }: ImageProps) => {
  const dimensions = useDimensions({ media, width, height, ...props });

  const resolvedTitle = useResolvedLocalizedObject(title ?? '');
  const resolvedAlt = useResolvedLocalizedObject(alt ?? '');
  const { projectSettings } = useProjectSettings();
  const { proxySuffix, placeHolderImage, imageQuality = 75 } = projectSettings ?? {};
  const PLACEHOLDER_IMAGE = placeHolderImage ?? '/images/placeholder.jpg';

  const cropConfig = useMemo(() => getCropConfig(gravity), [gravity]);

  // Handle Cloudinary images (with mediaId)
  if (media?.mediaId) {
    const gravityMode =
      gravity?.mode ??
      (!cropType ||
        ['auto', 'crop', 'fill', 'lfill', 'fill_pad', 'thumb'].includes(cropType.toLowerCase())
        ? 'auto'
        : undefined);
    return (
      <CldImage
        src={media.mediaId}
        alt={resolvedTitle || resolvedAlt}
        title={resolvedTitle}
        crop={cropType ?? cropConfig}
        gravity={
          gravityMode !== 'auto' ||
            !cropType ||
            ['auto', 'crop', 'fill', 'lfill', 'fill_pad', 'thumb'].includes(cropType.toLowerCase())
            ? gravityMode
            : undefined
        }
        aspectRatio={ratio}
        {...dimensions}
        {...props}
        quality={'auto:best'}
        dpr={'auto'}
      />
    );
  }

  // Handle regular images with Next.js Image
  try {
    // Attempt to process the src with the loader
    const processedSrc = defaultLoader({ src, suffix, proxySuffix });

    // If we got a valid URL, use it
    if (src && processedSrc && typeof processedSrc === 'string' && processedSrc.length > 0) {
      return (
        <NextImage
          src={processedSrc.trim()}
          alt={resolvedAlt}
          title={resolvedTitle}
          {...dimensions}
          {...props}
          quality={imageQuality}
          loader={netlifyLoader}
        />
      );
    }
  } catch (error) {
    console.warn('Error processing image source:', error);
  }

  // Fallback to placeholder if anything goes wrong
  return <NextImage src={PLACEHOLDER_IMAGE} alt={resolvedAlt} title={resolvedTitle} quality={imageQuality} {...dimensions} {...props} />;
};

export default Image;

export * from './types';
